import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';

const cancelBtnClass = 'inline-flex items-center justify-center px-4 py-2 border border-rule '
  + 'rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition';
const saveBtnClass = 'inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white '
  + 'rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 '
  + 'disabled:cursor-not-allowed transition';

/**
 * Admin-only editor for the reporting manager shown on a member's profile.
 * Writes the same member field the Manage Members screen edits, so the two
 * surfaces stay in step and the org chart follows immediately.
 */
function ReportingManagerModal({ member, closeModal, onUpdated }) {
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(member?.reportingManager || '');

  useEffect(() => {
    const fetchManagers = async () => {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.reportingManagers(activeWorkspace.id),
      });
      if (response.success) {
        setManagers(response.data.reportingManagers || []);
      } else {
        displayNotification({ message: 'Could not load the list of managers.', severity: 'error' });
      }
      setLoading(false);
    };

    fetchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace.id]);

  const handleSave = async () => {
    setSaving(true);

    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.editMember(activeWorkspace.id, member.id),
      data: { updateData: { reportingManager: selected || null } },
    });

    setSaving(false);

    if (response.success) {
      displayNotification({ message: 'Reporting manager updated.', severity: 'success' });
      await onUpdated();
      closeModal();
    } else {
      displayNotification({
        message: response.data?.reportingManager || 'Could not update the reporting manager.',
        severity: 'error',
      });
    }
  };

  const unchanged = (selected || '') === (member?.reportingManager || '');

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c"
      data-testid="reporting-manager-modal"
    >
      <Box style={{
        width: 480,
        maxWidth: 'calc(100% - 32px)',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
      }}
      >
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-2" data-testid="modal-title">
          Reporting Manager
        </h5>
        <p className="text-sm text-ink-500 mb-5">
          Who this member reports to. This drives the org chart, alert
          acknowledgements and approvals for their requests.
        </p>

        <span className="block text-sm font-medium text-ink-900 mb-1.5">Reporting To</span>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : '')}
          disabled={loading || saving}
          data-testid="reporting-manager-select"
          className={`${STYLED_SELECT_CLASS} w-full`}
          style={STYLED_SELECT_STYLE}
        >
          <option value="">No reporting manager</option>
          {managers.filter((m) => m.id !== member.id).map((m) => (
            <option key={m.id} value={m.id}>{`${m.user.firstName || ''} ${m.user.lastName || ''}`.trim()}</option>
          ))}
        </select>

        <div className="flex justify-start items-center gap-2 mt-6">
          <button
            type="button"
            onClick={closeModal}
            data-testid="reporting-manager-cancel"
            className={cancelBtnClass}
            style={{ borderStyle: 'solid' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving || unchanged}
            data-testid="reporting-manager-save"
            className={saveBtnClass}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ReportingManagerModal.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number,
    reportingManager: PropTypes.number,
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
};

export default ReportingManagerModal;
